import * as React from 'react';
import { CircularProgress, makeStyles, Theme } from '@material-ui/core';
import { neutral, blue } from '../theme/colors';
import { FilterList } from '@material-ui/icons';
import TextStyles, { fontFamilies } from '../theme/text';
import FilterGroup from './filter-group';
import Scrollbars from 'react-custom-scrollbars';
import { SecondaryButton, TertiaryButton } from '../buttons';
import clsx from 'clsx';

export interface Filter {
  title: string;
  id: string;
  filters: {
    id: string | number;
    name: string;
  }[];
  defaultFilters?: Array<string | number>;
}

export interface ActiveFilter extends Filter {
  active: Array<string | number>;
}

export interface FilterMenuProps {
  /** state of parent collapsible sidebar */
  isCollapsed?: boolean;
  activeFilters: ActiveFilter[];
  setActiveFilters: React.Dispatch<React.SetStateAction<ActiveFilter[]>>;
  /** Optional prop if loading the filters from an external source in the parent component */
  loading?: boolean;
}

const useStyles = makeStyles<Theme, { isCollapsed?: boolean }>({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  header: (props) => ({
    height: 43,
    padding: 8,
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: fontFamilies.heading,
    borderBottom: props.isCollapsed ? 'none' : `1px solid ${neutral[300]}`
  }),
  iconWrapper: (props) => ({
    width: props.isCollapsed ? '100%' : 'auto',
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      display: 'block',
      margin: props.isCollapsed ? '0 auto' : '0 5px 0 0'
    },
    '& .filters': {
      margin: 0,
      fontWeight: 600,
      fontSize: '1rem'
    }
  }),
  loadingFilters: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '12px'
  },
  inactive: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '12px',
    ...TextStyles.label1,
    whiteSpace: 'nowrap',
    opacity: 1,
    transition: 'opacity 150ms',
    '&.collapsed': {
      opacity: 0
    }
  },
  filterActions: (props) => ({
    visibility: props.isCollapsed ? 'hidden' : 'visible',
    borderBottom: `1px solid ${neutral[300]}`,
    padding: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& button:first-of-type': {
      marginRight: 8
    }
  }),
  filterGroup: (props) => ({
    visibility: props.isCollapsed ? 'hidden' : 'visible',
    padding: 8,
    whiteSpace: 'nowrap',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${neutral[300]}`
    }
  }),
  clearFilters: {
    display: 'flex',
    alignItems: 'center'
  },
  chip: {
    fontSize: 12,
    lineHeight: '14px',
    fontFamily: fontFamilies.body,
    color: 'white',
    backgroundColor: blue[500],
    padding: '0 8px',
    borderRadius: '8px'
  }
});

/**
 * Render filter sidebar content
 * @param isCollapsed - collapse state of parent collapsible sidebar component
 * @param setActiveFilters - state dispatch for active filters from parent view
 */
const FilterMenu = ({ isCollapsed, activeFilters, setActiveFilters, loading }: FilterMenuProps) => {
  const classes = useStyles({ isCollapsed });
  const [selectedFilters, setSelectedFilters] = React.useState<ActiveFilter[]>(activeFilters);
  const [activeCount, setActiveCount] = React.useState<number>(0);
  const totalFilters = activeFilters.reduce((acc, cur) => acc + cur.filters.length, 0);

  React.useEffect(() => {
    setActiveCount(activeFilters.reduce((acc, cur) => acc + cur.active.length, 0));
    setSelectedFilters(activeFilters);
  }, [activeFilters]);

  const handleActivation = () => {
    setActiveFilters(selectedFilters);
  };

  const handleCancel = () => {
    setSelectedFilters(activeFilters);
  };

  const updateSelectedFilters = (title: string, selected: Array<string | number>) => {
    const updated = selectedFilters.map((filterGroup) => {
      if (filterGroup.title == title) {
        return {
          title,
          id: filterGroup.id,
          filters: filterGroup.filters,
          active: selected
        };
      }
      return filterGroup;
    });
    setSelectedFilters(updated);
  };

  const clearAll = () => {
    const cleared = activeFilters.map((filter) => ({
      ...filter,
      active: filter.filters.map((f) => f.id)
    }));
    setActiveFilters(cleared);
    setSelectedFilters(cleared);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={classes.loadingFilters}>
          <CircularProgress size={20} />
        </div>
      );
    } else if (activeFilters.length == 0) {
      return (
        <div className={clsx(classes.inactive, isCollapsed && 'collapsed')}>
          <p>No Filters Available</p>
        </div>
      );
    }
    return (
      <React.Fragment>
        <div className={classes.filterActions}>
          <TertiaryButton
            component="button"
            text="cancel"
            title="cancel"
            handleClick={handleCancel}
          />
          <SecondaryButton text="Apply" title="Apply" handleClick={handleActivation} />
        </div>
        <Scrollbars style={{ flex: 1 }} hidden={isCollapsed}>
          {selectedFilters.map((filterGroup) => (
            <div className={classes.filterGroup} key={filterGroup.id}>
              <FilterGroup
                title={filterGroup.title}
                filters={filterGroup.filters}
                selected={filterGroup.active}
                updateSelectedFilters={updateSelectedFilters}
              />
            </div>
          ))}
        </Scrollbars>
      </React.Fragment>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.iconWrapper}>
          <FilterList />
          {!isCollapsed && <p className="filters">Filters</p>}
        </div>
        {!isCollapsed && totalFilters != activeCount && (
          <div className={classes.clearFilters}>
            <TertiaryButton
              component="button"
              title="Clear All"
              text="Clear All"
              handleClick={clearAll}
              className={classes.clearButton}
            />
            <span className={classes.chip}>{activeCount}</span>
          </div>
        )}
      </div>
      {renderContent()}
    </div>
  );
};

export default FilterMenu;
